import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonDatetime } from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import { TimeSlot } from '../../home/home.page';

/**
 * Date / time slot step of the customer form: calendar card, time slot pills and the selection recap.
 * Presentational only: the page keeps the state and handles the emitted intents.
 */
@Component({
  selector: 'app-date-slot-picker',
  templateUrl: './date-slot-picker.component.html',
  styleUrls: ['./date-slot-picker.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateModule, IonDatetime],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[style.--slot-accent]': 'accentColor' }
})
export class DateSlotPickerComponent {
  @Input() min: string = '';
  @Input() max: string = '';
  @Input() highlightedDates?: (isoString: string) => any;
  @Input() isDateEnabled?: (isoString: string) => boolean;
  @Input() hasPrivatizedException: boolean = false;
  @Input() hasDatePromotions: boolean = false;
  @Input() isTimeSlot: boolean = false;
  @Input() timeSlots: TimeSlot[] = [];
  @Input() selectedDate: Date | null = null;
  @Input() selectedSlot: TimeSlot | null = null;
  // Quantity of this ticket type already in the cart: a slot is full when it can't host it
  @Input() reservedQuantity: number = 0;
  // Slot duration displayed in the recap (null: not displayed)
  @Input() durationMinutes: number | null = null;
  // Side color of the cards (null: primary)
  @Input() accentColor: string | null = null;
  @Output() dateChange = new EventEmitter<any>();
  @Output() slotToggle = new EventEmitter<TimeSlot>();

  @ViewChild(IonDatetime) private datetime?: IonDatetime;

  /** Moves the calendar viewport to a date without selecting it. */
  resetCalendar(dateISO: string): Promise<void> | undefined {
    return this.datetime?.reset(dateISO);
  }

  /** Remaining tickets of the selected date/slot when scarce (< 20), null otherwise. */
  get remainingTickets(): number | null {
    if (!this.selectedDate || !this.selectedSlot?.capacity) return null;
    const remaining = this.selectedSlot.capacity - this.selectedSlot.booked;
    return remaining < 20 ? remaining : null;
  }

  isSlotFull(slot: TimeSlot): boolean {
    return slot.booked + this.reservedQuantity > slot.capacity;
  }

  removeSelectedSlot(): void {
    if (this.selectedSlot) this.slotToggle.emit(this.selectedSlot);
  }

  trackBySlotLabel(_index: number, slot: TimeSlot): string {
    return slot.label;
  }
}
